import styled from "styled-components"
import Header from "../Common/Header"
import Footer from "../Common/Footer"
import ConsultationForm from "@components/forms/ConsultationForm"

const PageWrapper = styled.div`
  background: #f2f6fa;
  color: #172c42;
`

const HeroSection = styled.section`
  position: relative;
  min-height: 720px;
  display: flex;
  align-items: flex-end;
  padding: clamp(4rem, 8vw, 7rem) 0;
  isolation: isolate;
  overflow: hidden;
  color: #ffffff;
  background: #071b30 url("/img/cta-background.jpg") center 48% / cover no-repeat;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(90deg, rgba(5, 20, 36, 0.96) 0%, rgba(7, 27, 47, 0.88) 48%, rgba(7, 27, 47, 0.4) 100%);
  }

  @media (max-width: 800px) {
    min-height: auto;
  }
`

const HeroInner = styled.div`
  width: min(1280px, 90vw);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(300px, 0.55fr);
  gap: clamp(3rem, 8vw, 8rem);
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const Kicker = styled.p`
  margin: 0 0 1rem;
  color: #92c9f4;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.17em;
  text-transform: uppercase;
`

const HeroTitle = styled.h1`
  max-width: 900px;
  color: #ffffff;
  font-size: clamp(3rem, 6vw, 5.8rem);
  line-height: 0.94;
  letter-spacing: -0.06em;
`

const HeroSubtitle = styled.p`
  max-width: 720px;
  margin: 1.75rem 0 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: clamp(1.05rem, 1.7vw, 1.24rem);
  line-height: 1.7;
`

const HeroSpecs = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.28);
  border-bottom: 1px solid rgba(255, 255, 255, 0.28);

  span {
    padding: 0.75rem 1.1rem;
    border-right: 1px solid rgba(255, 255, 255, 0.28);
    color: rgba(255, 255, 255, 0.76);
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
`

const PrimaryCTA = styled.a`
  min-height: 54px;
  display: inline-flex;
  align-items: center;
  padding: 0 1.5rem;
  border: 1px solid #ffffff;
  border-radius: 3px;
  background: #ffffff;
  color: #0d365d;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    background: transparent;
    color: #ffffff;
  }
`

const SecondaryCTA = styled.a`
  min-height: 54px;
  display: inline-flex;
  align-items: center;
  padding: 0 1rem;
  color: rgba(255, 255, 255, 0.82);
  font-weight: 700;
  text-decoration: none;

  &::after {
    content: "↘";
    margin-left: 0.65rem;
  }
`

const HeroLedger = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.42);
`

const LedgerItem = styled.div`
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.26);

  h2 {
    color: #ffffff;
    font-size: 1rem;
  }

  p {
    margin: 0.45rem 0 0;
    color: rgba(255, 255, 255, 0.68);
    font-size: 0.9rem;
    line-height: 1.55;
  }
`

const ContentWrapper = styled.div`
  padding: clamp(5rem, 10vw, 9rem) 0;
  display: grid;
  gap: clamp(6rem, 12vw, 11rem);
`

const Section = styled.section`
  width: min(1280px, 90vw);
  margin: 0 auto;
`

const SectionHeader = styled.div`
  display: grid;
  grid-template-columns: 170px minmax(0, 760px);
  gap: 2rem;
  margin-bottom: clamp(2.5rem, 6vw, 5rem);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`

const SectionLabel = styled.p`
  margin: 0;
  color: #337abd;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`

const SectionTitle = styled.h2`
  color: #102f4f;
  font-size: clamp(2.5rem, 5vw, 4.7rem);
  line-height: 0.98;
  letter-spacing: -0.055em;
`

const Comparison = styled.div`
  border-top: 1px solid #aebfce;
`

const ComparisonRow = styled.div<{ $head?: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-bottom: 1px solid #aebfce;
  color: ${({ $head }) => ($head ? "#123a60" : "#485f74")};
  font-weight: ${({ $head }) => ($head ? 800 : 400)};

  > * {
    padding: ${({ $head }) => ($head ? "1rem 1.5rem" : "1.4rem 1.5rem")};
    line-height: 1.6;
  }

  > *:first-child {
    border-right: 1px solid #aebfce;
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;

    > *:first-child {
      border-right: 0;
      border-bottom: 1px dashed #c4d0da;
    }
  }
`

const ResponsibilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border: 1px solid #b9c9d6;
  background: #ffffff;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

const Responsibility = styled.article`
  padding: clamp(2rem, 5vw, 4rem);

  & + & {
    border-left: 1px solid #b9c9d6;
  }

  h3 {
    color: #123a60;
    font-size: clamp(1.6rem, 3vw, 2.35rem);
    letter-spacing: -0.035em;
  }

  @media (max-width: 760px) {
    & + & {
      border-left: 0;
      border-top: 1px solid #b9c9d6;
    }
  }
`

const List = styled.ul`
  list-style: none;
  margin: 1.75rem 0 0;
  padding: 0;
  border-top: 1px solid #d0dbe4;

  li {
    padding: 1rem 0;
    border-bottom: 1px solid #d0dbe4;
    color: #52687c;
    line-height: 1.6;
  }
`

const FormSection = styled.section`
  width: min(1280px, 90vw);
  margin: 0 auto;
`

const FAQGroup = styled.div`
  border-top: 1px solid #aebfce;
`

const FAQItem = styled.details`
  border-bottom: 1px solid #aebfce;

  summary {
    position: relative;
    padding: 1.4rem 3rem 1.4rem 0;
    color: #173a5d;
    font-size: 1.05rem;
    font-weight: 800;
    cursor: pointer;
    list-style: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary::after {
    content: "+";
    position: absolute;
    right: 0;
    color: #337abd;
    font-size: 1.4rem;
    font-weight: 400;
  }

  &[open] summary::after {
    content: "−";
  }

  p {
    max-width: 850px;
    margin: 0 0 1.5rem;
    color: #52687c;
    line-height: 1.7;
  }
`

const GestionChaletsPortneuf = () => (
  <PageWrapper>
    <Header />
    <main id="contenu-principal">
      <HeroSection>
        <HeroInner>
          <div>
            <Kicker>Chalets et condos · Portneuf</Kicker>
            <HeroTitle>Une opération fluide, séjour après séjour.</HeroTitle>
            <HeroSubtitle>
              Nous coordonnons les réservations, les communications avec les voyageurs, le ménage et l’entretien à Portneuf et dans la couronne ouest
              de Québec.
            </HeroSubtitle>
            <HeroSpecs>
              <span>Chalets et condos</span>
              <span>Soutien 7 j / 7</span>
              <span>Tarifs suivis</span>
            </HeroSpecs>
            <HeroActions>
              <PrimaryCTA href="#analyse">Parler de votre propriété</PrimaryCTA>
              <SecondaryCTA href="#comparatif">Comparer les modes de gestion</SecondaryCTA>
            </HeroActions>
          </div>
          <HeroLedger>
            <LedgerItem>
              <h2>Coordination des séjours</h2>
              <p>Arrivées, départs, ménage, inventaire et rapports essentiels.</p>
            </LedgerItem>
            <LedgerItem>
              <h2>Tarifs suivis</h2>
              <p>Ajustements selon la saison, la demande et les paramètres convenus.</p>
            </LedgerItem>
            <LedgerItem>
              <h2>Échéances administratives</h2>
              <p>Enregistrement, taxe sur l’hébergement et règles municipales au calendrier.</p>
            </LedgerItem>
          </HeroLedger>
        </HeroInner>
      </HeroSection>

      <ContentWrapper>
        <Section id="comparatif">
          <SectionHeader>
            <SectionLabel>Comparatif</SectionLabel>
            <SectionTitle>Gérer soi-même ou déléguer les opérations.</SectionTitle>
          </SectionHeader>
          <Comparison>
            <ComparisonRow $head>
              <span>Gestion par le propriétaire</span>
              <span>TB Gestion Immobilière</span>
            </ComparisonRow>
            <ComparisonRow>
              <span>Répondre aux demandes, gérer les arrivées et coordonner le ménage.</span>
              <span>Coordination locale, calendrier partagé et suivi après les séjours.</span>
            </ComparisonRow>
            <ComparisonRow>
              <span>Établir et ajuster les tarifs et les disponibilités.</span>
              <span>Tarifs et disponibilités suivis selon la fréquence prévue au mandat.</span>
            </ComparisonRow>
            <ComparisonRow>
              <span>Suivre les contrats, taxes et échéances applicables.</span>
              <span>Calendrier administratif documenté et coordination des professionnels requis.</span>
            </ComparisonRow>
          </Comparison>
        </Section>

        <Section>
          <SectionHeader>
            <SectionLabel>Portée du mandat</SectionLabel>
            <SectionTitle>Des responsabilités clairement réparties.</SectionTitle>
          </SectionHeader>
          <ResponsibilityGrid>
            <Responsibility>
              <h3>Opérations et séjours</h3>
              <List>
                <li>Création, mise à jour et suivi des annonces sur les canaux retenus.</li>
                <li>Calendrier synchronisé, confirmations et soutien aux voyageurs.</li>
                <li>Inspection après les séjours, inventaire et entretien préventif.</li>
              </List>
            </Responsibility>
            <Responsibility>
              <h3>Suivi administratif</h3>
              <List>
                <li>Accompagnement pour l’enregistrement et les renouvellements requis.</li>
                <li>Suivi des échéances liées à la taxe sur l’hébergement selon le mandat.</li>
                <li>Vérification des règles municipales à considérer.</li>
              </List>
            </Responsibility>
          </ResponsibilityGrid>
        </Section>

        <FormSection id="analyse">
          <ConsultationForm
            inquiryType="Gestion chalets et condos"
            title="Parlons de votre location touristique"
            description="Partagez les informations essentielles sur votre propriété. Nous examinerons les services à déléguer et les vérifications administratives à prévoir."
            additionalFields={[
              {
                name: "propertyType",
                label: "Type d'hébergement",
                type: "select",
                options: [
                  { label: "Chalet", value: "Chalet" },
                  { label: "Condo", value: "Condo" },
                  { label: "Maison de ville", value: "Maison de ville" },
                  { label: "Autre", value: "Autre" },
                ],
              },
              {
                name: "rentalSeason",
                label: "Périodes de location souhaitées",
                type: "select",
                options: [
                  { label: "Année complète", value: "Année complète" },
                  { label: "Haute saison seulement", value: "Haute saison seulement" },
                  { label: "Week-ends et congés", value: "Week-ends et congés" },
                ],
              },
              {
                name: "platforms",
                label: "Plateformes utilisées",
                type: "textarea",
                placeholder: "Airbnb, VRBO, site web, réseaux sociaux…",
                fullWidth: true,
              },
            ]}
          />
        </FormSection>

        <Section>
          <SectionHeader>
            <SectionLabel>Questions fréquentes</SectionLabel>
            <SectionTitle>Avant de nous confier les opérations.</SectionTitle>
          </SectionHeader>
          <FAQGroup>
            <FAQItem>
              <summary>Comment gérez-vous le ménage et l'entretien?</summary>
              <p>Nous travaillons avec des équipes locales selon les standards convenus et une liste de vérification après chaque séjour.</p>
            </FAQItem>
            <FAQItem>
              <summary>Pouvez-vous m'aider à obtenir ou renouveler l'attestation CITQ?</summary>
              <p>
                Nous pouvons vous accompagner dans la préparation du dossier et le suivi des échéances. Le propriétaire demeure responsable des
                renseignements fournis et des exigences applicables.
              </p>
            </FAQItem>
            <FAQItem>
              <summary>Accompagnez-vous la mise en marché internationale?</summary>
              <p>Selon le mandat, nous pouvons adapter ou traduire les annonces et les communications destinées à une clientèle hors Québec.</p>
            </FAQItem>
          </FAQGroup>
        </Section>
      </ContentWrapper>
    </main>
    <Footer />
  </PageWrapper>
)

export default GestionChaletsPortneuf
